// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize } from "../utils";
import { apiCall } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";

export function ResponsePage({ token }: { token?: string }) {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<any>(null);
  const [response, setResponse] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetch(`/api/respond/${token}`)
      .then(r => { if (!r.ok) return r.json().then(d => { throw new Error(d.error); }); return r.json(); })
      .then(d => { if (d.responded) setSubmitted(true); setData(d); })
      .catch(err => setError(err.message));
  }, [token]);

  const handleSubmit = async () => {
    if (!response.trim()) return;
    setSubmitting(true);
    try {
      const r = await fetch(`/api/respond/${token}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ response: response.trim() }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setSubmitted(true);
    } catch (err: any) { setError(err.message); }
    setSubmitting(false);
  };

  const box: CSSProperties = { maxWidth: 560, margin: "60px auto", padding: 32, fontFamily: "'Inter', -apple-system, sans-serif" };
  const hdr: CSSProperties = { textAlign: "center", marginBottom: 32 };

  if (error) return (
    <div style={box}>
      <div style={hdr}><div style={{ fontSize: 22, fontWeight: 800, color: "#0D6B3D", letterSpacing: "-.02em" }}>KontractAI</div></div>
      <div style={{ padding: 24, backgroundColor: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, textAlign: "center" }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>!</div>
        <div style={{ fontSize: 14, color: "#DC2626", fontWeight: 600, marginBottom: 4 }}>{error}</div>
        <div style={{ fontSize: 12, color: "#666" }}>Please contact your legal team for assistance.</div>
      </div>
    </div>
  );

  if (!data) return (
    <div style={box}>
      <div style={hdr}><div style={{ fontSize: 22, fontWeight: 800, color: "#0D6B3D", letterSpacing: "-.02em" }}>KontractAI</div></div>
      <div style={{ textAlign: "center", color: "#666", fontSize: 13 }}>Loading...</div>
    </div>
  );

  if (submitted) return (
    <div style={box}>
      <div style={hdr}><div style={{ fontSize: 22, fontWeight: 800, color: "#0D6B3D", letterSpacing: "-.02em" }}>KontractAI</div></div>
      <div style={{ padding: 24, backgroundColor: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, textAlign: "center" }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>&#10003;</div>
        <div style={{ fontSize: 15, color: "#166534", fontWeight: 700, marginBottom: 6 }}>Response Submitted</div>
        <div style={{ fontSize: 13, color: "#555", lineHeight: 1.5 }}>Thank you for providing the requested information for <strong>"{data.title}"</strong>. Your legal team will review shortly.</div>
      </div>
    </div>
  );

  return (
    <div style={box}>
      <div style={hdr}><div style={{ fontSize: 22, fontWeight: 800, color: "#0D6B3D", letterSpacing: "-.02em" }}>KontractAI</div><div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>Additional Information Requested</div></div>
      <div style={{ backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#1A1A1A", marginBottom: 12 }}>{data.title}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12, color: "#555" }}>
          <div><span style={{ color: "#888", textTransform: "uppercase", fontSize: 9, letterSpacing: ".04em" }}>Counterparty</span><div style={{ fontWeight: 500 }}>{data.counterparty}</div></div>
          <div><span style={{ color: "#888", textTransform: "uppercase", fontSize: 9, letterSpacing: ".04em" }}>Type</span><div style={{ fontWeight: 500 }}>{data.requestType}</div></div>
        </div>
      </div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#333", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 }}>What's Needed</div>
        <div style={{ padding: 14, backgroundColor: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 6, fontSize: 13, color: "#92400E", lineHeight: 1.5 }}>{data.reason}</div>
      </div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#333", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 }}>Your Response</div>
        <textarea value={response} onChange={e => setResponse(e.target.value)} placeholder="Provide the requested information here..." rows={5} style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #D1D5DB", fontSize: 13, fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box", lineHeight: 1.5 }} />
      </div>
      <button onClick={() => { handleSubmit(); }} disabled={submitting || !response.trim()} style={{ width: "100%", padding: "12px 24px", backgroundColor: submitting || !response.trim() ? "#9CA3AF" : "#0D6B3D", color: "#fff", border: "none", borderRadius: 6, fontSize: 14, fontWeight: 700, cursor: submitting || !response.trim() ? "default" : "pointer", fontFamily: "inherit" }}>{submitting ? "Submitting..." : "Submit Response"}</button>
      <div style={{ textAlign: "center", marginTop: 12, fontSize: 11, color: "#999" }}>This link expires 14 days after it was created.</div>
    </div>
  );
}

/* ═══════════════════ REPORTS VIEW ═══════════════════ */
